import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { LexicalTypeaheadMenuPlugin, MenuOption, type MenuTextMatch } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { $getSelection, $insertNodes, $isRangeSelection, COMMAND_PRIORITY_CRITICAL, type TextNode } from 'lexical'
import { type FC, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './EmailEditor.module.scss'
import { type EmailVariable } from './emailTemplateTypes'
import { $findSlot } from './nodes/SlotNode'
import { $createVariableNode } from './nodes/VariableNode'
import { matchVariables, matchVariableTrigger, orderVariables } from './variableGroups'
import { VariableMenu } from './VariableMenu'

class VariableOption extends MenuOption {
  variable: EmailVariable

  constructor(variable: EmailVariable) {
    super(variable.name)
    this.variable = variable
  }
}

export interface VariableTypeaheadPluginProps {
  variables: EmailVariable[]
}

export const VariableTypeaheadPlugin: FC<VariableTypeaheadPluginProps> = ({ variables }) => {
  const [editor] = useLexicalComposerContext()
  const [query, setQuery] = useState<string | null>(null)
  const options = useMemo(
    () =>
      orderVariables(
        matchVariables(
          variables.filter((variable) => variable.insertable),
          query ?? '',
        ),
      ).map((variable) => new VariableOption(variable)),
    [variables, query],
  )

  const triggerFn = useCallback((text: string): MenuTextMatch | null => {
    const selection = $getSelection()
    const slot = $isRangeSelection(selection) ? $findSlot(selection.anchor.getNode()) : null
    return slot?.isEditable() ? matchVariableTrigger(text) : null
  }, [])

  const onSelectOption = useCallback(
    (option: VariableOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const chip = $createVariableNode(option.variable.name, option.variable.label)
        if (nodeToReplace) {
          nodeToReplace.replace(chip)
        } else {
          $insertNodes([chip])
        }
        chip.selectNext(0, 0)
        closeMenu()
      })
    },
    [editor],
  )

  return (
    <LexicalTypeaheadMenuPlugin<VariableOption>
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      options={options}
      commandPriority={COMMAND_PRIORITY_CRITICAL}
      anchorClassName={styles.variableTypeahead}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        anchorElementRef.current && options.length
          ? createPortal(
              <VariableMenu
                variables={options.map((option) => option.variable)}
                highlighted={selectedIndex ?? 0}
                optionId={(index) => `typeahead-item-${index}`}
                onHighlight={setHighlightedIndex}
                onPick={(variable) => {
                  const picked = options.find((option) => option.variable === variable)
                  if (picked) {
                    selectOptionAndCleanUp(picked)
                  }
                }}
              />,
              anchorElementRef.current,
            )
          : null
      }
    />
  )
}
